import React, { useState, useEffect, useCallback } from 'react';
import { projectApi } from '../services/api';
import { useProject } from '../App';
import { formatTimestamp } from '../utils/format';

const POLL_INTERVAL = parseInt(process.env.REACT_APP_POLL_INTERVAL || '5000', 10);

function StatusBadge({ status }) {
    const colors = {
        ACTIVE: 'bg-green-100 text-green-800',
        STOPPED: 'bg-yellow-100 text-yellow-800',
        ERROR: 'bg-red-100 text-red-800',
        CREATING: 'bg-blue-100 text-blue-800',
    };
    return (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
            {status}
        </span>
    );
}

function DbTypeIcon({ type }) {
    const icons = { oracle: 'O', postgresql: 'P', postgres: 'P', mysql: 'M' };
    return (
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 text-gray-700 font-bold text-sm">
            {icons[type?.toLowerCase()] || '?'}
        </span>
    );
}

function CreateProjectModal({ onClose, onCreated }) {
    const [form, setForm] = useState({
        name: '',
        displayName: '',
        description: '',
        sourceDb: { type: 'oracle', hostname: '', port: 1521, database: '', schema: '', username: '', password: '', tables: [] },
    });
    const [tablesInput, setTablesInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const dbDefaults = {
        oracle: { port: 1521, schema: 'APP_USER' },
        postgresql: { port: 5432, schema: 'public' },
        mysql: { port: 3306, schema: '' },
    };

    const handleTypeChange = (type) => {
        const defaults = dbDefaults[type] || {};
        setForm({
            ...form,
            sourceDb: { ...form.sourceDb, type, port: defaults.port || 1521, schema: defaults.schema || '' },
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const project = {
                ...form,
                sourceDb: {
                    ...form.sourceDb,
                    tables: tablesInput.split(',').map((t) => t.trim()).filter(Boolean),
                },
            };
            await projectApi.createProject(project);
            onCreated();
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <h2 className="text-xl font-bold mb-4">Create Project</h2>
                {error && <div className="bg-red-50 text-red-700 p-2 rounded mb-3 text-sm">{error}</div>}
                <form onSubmit={handleSubmit} className="space-y-3">
                    <div>
                        <label className="block text-sm font-medium mb-1">Project Name *</label>
                        <input type="text" required pattern="^[a-zA-Z][a-zA-Z0-9_-]*"
                            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="w-full border rounded px-3 py-2 text-sm" placeholder="cusOracleAudit" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Display Name</label>
                        <input type="text" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                            className="w-full border rounded px-3 py-2 text-sm" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Description</label>
                        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                            className="w-full border rounded px-3 py-2 text-sm" rows="2" />
                    </div>
                    <div className="border-t pt-3">
                        <h3 className="font-medium mb-2">Source Database</h3>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm mb-1">Type</label>
                                <select value={form.sourceDb.type} onChange={(e) => handleTypeChange(e.target.value)}
                                    className="w-full border rounded px-3 py-2 text-sm">
                                    <option value="oracle">Oracle</option>
                                    <option value="postgresql">PostgreSQL</option>
                                    <option value="mysql">MySQL</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm mb-1">Port</label>
                                <input type="number" value={form.sourceDb.port}
                                    onChange={(e) => setForm({ ...form, sourceDb: { ...form.sourceDb, port: parseInt(e.target.value) } })}
                                    className="w-full border rounded px-3 py-2 text-sm" />
                            </div>
                        </div>
                        <div className="mt-3">
                            <label className="block text-sm mb-1">Hostname *</label>
                            <input type="text" required value={form.sourceDb.hostname}
                                onChange={(e) => setForm({ ...form, sourceDb: { ...form.sourceDb, hostname: e.target.value } })}
                                className="w-full border rounded px-3 py-2 text-sm" placeholder="localhost" />
                        </div>
                        <div className="grid grid-cols-2 gap-3 mt-3">
                            <div>
                                <label className="block text-sm mb-1">Database *</label>
                                <input type="text" required value={form.sourceDb.database}
                                    onChange={(e) => setForm({ ...form, sourceDb: { ...form.sourceDb, database: e.target.value } })}
                                    className="w-full border rounded px-3 py-2 text-sm" />
                            </div>
                            <div>
                                <label className="block text-sm mb-1">Schema</label>
                                <input type="text" value={form.sourceDb.schema}
                                    onChange={(e) => setForm({ ...form, sourceDb: { ...form.sourceDb, schema: e.target.value } })}
                                    className="w-full border rounded px-3 py-2 text-sm" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 mt-3">
                            <div>
                                <label className="block text-sm mb-1">Username *</label>
                                <input type="text" required value={form.sourceDb.username}
                                    onChange={(e) => setForm({ ...form, sourceDb: { ...form.sourceDb, username: e.target.value } })}
                                    className="w-full border rounded px-3 py-2 text-sm" />
                            </div>
                            <div>
                                <label className="block text-sm mb-1">Password *</label>
                                <input type="password" required value={form.sourceDb.password}
                                    onChange={(e) => setForm({ ...form, sourceDb: { ...form.sourceDb, password: e.target.value } })}
                                    className="w-full border rounded px-3 py-2 text-sm" />
                            </div>
                        </div>
                        <div className="mt-3">
                            <label className="block text-sm mb-1">Tables (comma-separated)</label>
                            <input type="text" value={tablesInput} onChange={(e) => setTablesInput(e.target.value)}
                                className="w-full border rounded px-3 py-2 text-sm" placeholder="CUSTOMERS, PRODUCTS, ORDERS" />
                        </div>
                    </div>
                    <div className="flex justify-end space-x-2 pt-3">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-sm border rounded hover:bg-gray-50">Cancel</button>
                        <button type="submit" disabled={loading} className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                            {loading ? 'Creating...' : 'Create'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function ProjectCard({ project, onAction }) {
    const [expanded, setExpanded] = useState(false);
    const [operations, setOperations] = useState([]);
    const [loadingOps, setLoadingOps] = useState(false);

    const loadOperations = async () => {
        setLoadingOps(true);
        try {
            const res = await projectApi.getProjectOperations(project.name, 0, 10);
            setOperations(res.data?.content || []);
        } catch (err) {
            console.error('Failed to load operations:', err);
        } finally {
            setLoadingOps(false);
        }
    };

    const handleExpand = () => {
        if (!expanded) loadOperations();
        setExpanded(!expanded);
    };

    return (
        <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                    <DbTypeIcon type={project.sourceDb?.type} />
                    <div>
                        <h3 className="font-medium">{project.displayName || project.name}</h3>
                        <p className="text-sm text-gray-500">{project.name}</p>
                    </div>
                </div>
                <div className="flex items-center space-x-2">
                    <StatusBadge status={project.status} />
                    {project.debezium?.deployed && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">Connector</span>
                    )}
                    {project.kafka?.consumerRunning && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Consumer</span>
                    )}
                </div>
            </div>

            {project.description && <p className="text-sm text-gray-600 mt-2">{project.description}</p>}

            <div className="flex items-center space-x-2 mt-3">
                {!project.debezium?.deployed ? (
                    <button onClick={() => onAction('deploy', project.name)} className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700">
                        Deploy Connector
                    </button>
                ) : (
                    <button onClick={() => onAction('remove', project.name)} className="px-3 py-1 text-xs bg-purple-200 text-purple-800 rounded hover:bg-purple-300">
                        Remove Connector
                    </button>
                )}
                {!project.kafka?.consumerRunning ? (
                    <button onClick={() => onAction('start', project.name)} className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700">
                        Start Consumer
                    </button>
                ) : (
                    <button onClick={() => onAction('stop', project.name)} className="px-3 py-1 text-xs bg-yellow-200 text-yellow-800 rounded hover:bg-yellow-300">
                        Stop Consumer
                    </button>
                )}
                <button onClick={() => onAction('delete', project.name)} className="px-3 py-1 text-xs bg-red-200 text-red-800 rounded hover:bg-red-300">
                    Delete
                </button>
                <button onClick={handleExpand} className="px-3 py-1 text-xs bg-gray-200 text-gray-800 rounded hover:bg-gray-300">
                    {expanded ? 'Hide History' : 'Show History'}
                </button>
            </div>

            {expanded && (
                <div className="mt-3 border-t pt-3">
                    <h4 className="text-sm font-medium mb-2">Recent Operations</h4>
                    {loadingOps ? (
                        <p className="text-sm text-gray-500">Loading...</p>
                    ) : operations.length === 0 ? (
                        <p className="text-sm text-gray-500">No operations recorded</p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-gray-500">
                                    <th className="py-1">Time</th>
                                    <th className="py-1">Operation</th>
                                    <th className="py-1">Status</th>
                                    <th className="py-1">Detail</th>
                                </tr>
                            </thead>
                            <tbody>
                                {operations.map((op) => (
                                    <tr key={op.id} className="border-t">
                                        <td className="py-1 text-xs">{formatTimestamp(op.timestamp)}</td>
                                        <td className="py-1">{op.operation}</td>
                                        <td className="py-1">
                                            <span className={`px-1 py-0.5 rounded text-xs ${op.status === 'SUCCESS' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                {op.status}
                                            </span>
                                        </td>
                                        <td className="py-1 text-xs text-gray-600 truncate max-w-xs">{op.detail}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    );
}

function ProjectManagement() {
    const { projects, refreshProjects } = useProject();
    const [showCreate, setShowCreate] = useState(false);
    const [actionLoading, setActionLoading] = useState({});

    const loadProjects = useCallback(async () => {
        await refreshProjects();
    }, [refreshProjects]);

    useEffect(() => {
        loadProjects();
        const interval = setInterval(loadProjects, POLL_INTERVAL);
        return () => clearInterval(interval);
    }, [loadProjects]);

    const handleAction = async (action, name) => {
        const key = `${action}-${name}`;
        setActionLoading((prev) => ({ ...prev, [key]: true }));
        try {
            switch (action) {
                case 'deploy': await projectApi.deployConnector(name); break;
                case 'remove': await projectApi.removeConnector(name); break;
                case 'start': await projectApi.startConsumer(name); break;
                case 'stop': await projectApi.stopConsumer(name); break;
                case 'delete':
                    if (window.confirm(`Delete project "${name}" and all its data?`)) {
                        await projectApi.deleteProject(name, true);
                    }
                    break;
                default: break;
            }
        } catch (err) {
            alert(`Action failed: ${err.response?.data?.error || err.message}`);
        } finally {
            setActionLoading((prev) => ({ ...prev, [key]: false }));
            await loadProjects();
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-2xl font-bold">Project Management</h1>
                <button onClick={() => setShowCreate(true)} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                    Create Project
                </button>
            </div>

            {projects.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                    No projects yet. Create one to get started.
                </div>
            ) : (
                <div className="space-y-4">
                    {projects.map((project) => (
                        <ProjectCard key={project.name} project={project} onAction={handleAction} />
                    ))}
                </div>
            )}

            {showCreate && (
                <CreateProjectModal
                    onClose={() => setShowCreate(false)}
                    onCreated={() => { setShowCreate(false); loadProjects(); }}
                />
            )}
        </div>
    );
}

export default ProjectManagement;
